import { getFlagValue, isFlagEnabled } from '@Redux/actions/flagsmith';
import {
  introConstant,
  reasonConstant,
  closedConstant,
  popups as popupsConstant,
  CLOSE_ACCOUNT_FLAG,
  CLOSURE_ERROR_CODES
} from './constants';
import {
  CloseAccountContentful,
  CloseAccountPayload,
  CloseAccountPopup,
  ClosureReasonOption,
  PopupsData
} from './types';
import { store } from '../../../store';
import { APP_URL_ONELINK, HELP_CENTER, HISTORY_URL } from '@Utils/constants';

const userEmail = (): string =>
  store.getState()?.rootReducer?.userSessionReducer?.user_session?.email || '';

export const getCloseAccountScreenData = (contentfulData: CloseAccountContentful | null) => {
  //close account: intro close screen framed data
  const introData = contentfulData?.intro;
  const screen1 = {
    checklistItemsAmaze: {
      amazeWallet:
        introData?.checklistItemsAmaze?.amazeWallet ||
        introConstant?.checklistItemsAmaze?.amazeWallet,
      instaPoints:
        introData?.checklistItemsAmaze?.instaPoints ||
        introConstant?.checklistItemsAmaze?.instaPoints,
      transfers:
        introData?.checklistItemsAmaze?.transfers || introConstant?.checklistItemsAmaze?.transfers
    },
    checklistItemsNonAmaze: {
      instaPoints:
        introData?.checklistItemsNonAmaze?.instaPoints ||
        introConstant?.checklistItemsNonAmaze?.instaPoints,
      receipts:
        introData?.checklistItemsNonAmaze?.receipts ||
        introConstant?.checklistItemsNonAmaze?.receipts,
      transfers:
        introData?.checklistItemsNonAmaze?.transfers ||
        introConstant?.checklistItemsNonAmaze?.transfers
    },
    checklistTitle: introData?.checklistTitle || introConstant?.checklistTitle,
    description: introData?.description || introConstant?.description,
    raiseTicketCta: introData?.raiseTicketCta || introConstant?.raiseTicketCta,
    startCta: introData?.startCta || introConstant?.startCta,
    title: introData?.title || introConstant?.title,
    amazeWarning: introData?.amazeWarning || introConstant?.amazeWarning,
    nonAmazeWarning: introData?.nonAmazeWarning || introConstant?.nonAmazeWarning,
    needHelp: introData?.needHelp || introConstant?.needHelp,
    needHelpDescription: introData?.needHelpDescription || introConstant?.needHelpDescription
  };

  //close account:  reason screen framed data
  const reasonData = contentfulData?.reason;
  const screen2 = {
    confirmCheckbox: reasonData?.confirmCheckbox || reasonConstant?.confirmCheckbox,
    confirmCheckboxAmazeCustomer:
      reasonData?.confirmCheckboxAmazeCustomer || reasonConstant?.confirmCheckboxAmazeCustomer,
    options: {
      incorrectCountry:
        reasonData?.options?.incorrectCountry || reasonConstant?.options?.incorrectCountry,
      movedCountry: reasonData?.options?.movedCountry || reasonConstant?.options?.movedCountry,
      noLongerUsing: reasonData?.options?.noLongerUsing || reasonConstant?.options?.noLongerUsing,
      other: reasonData?.options?.other || reasonConstant?.options?.other
    },
    otherPlaceholder: reasonData?.otherPlaceholder || reasonConstant?.otherPlaceholder,
    submit: reasonData?.submit || reasonConstant?.submit,
    title: reasonData?.title || reasonConstant?.title
  };

  //close account:  final screen close account framed data
  const closedData = contentfulData?.closed;
  const screen3 = {
    cta: closedData?.cta || closedConstant?.cta,
    message: closedData?.message || closedConstant?.message,
    reRegisterMessage: closedData?.reRegisterMessage || closedConstant?.reRegisterMessage,
    supportMessage: closedData?.supportMessage || closedConstant?.supportMessage,
    title: closedData?.title || closedConstant?.title,
    needHelpTitle: closedData?.needHelpTitle || closedConstant?.needHelpTitle,
    needHelpDescription: closedData?.needHelpDescription || closedConstant?.needHelpDescription,
    reRegisterTitle: closedData?.reRegisterTitle || closedConstant?.reRegisterTitle
  };

  //close account:  all popups framed data
  const popupData = contentfulData?.popups;
  const popups = {
    amlHit: {
      title: popupData?.amlHit?.title || popupsConstant?.amlHit?.title,
      description: popupData?.amlHit?.description || popupsConstant?.amlHit?.description,
      primaryCta: popupData?.amlHit?.primaryCta || popupsConstant?.amlHit?.primaryCta
    },
    negativeBalance: {
      title: popupData?.negativeBalance?.title || popupsConstant?.negativeBalance?.title,
      description:
        popupData?.negativeBalance?.description || popupsConstant?.negativeBalance?.description,
      primaryCta:
        popupData?.negativeBalance?.primaryCta || popupsConstant?.negativeBalance?.primaryCta
    },
    transferInProgress: {
      title: popupData?.transferInProgress?.title || popupsConstant?.transferInProgress?.title,
      description:
        popupData?.transferInProgress?.description ||
        popupsConstant?.transferInProgress?.description,
      primaryCta:
        popupData?.transferInProgress?.primaryCta || popupsConstant?.transferInProgress?.primaryCta
    },
    unresolvedDispute: {
      title: popupData?.unresolvedDispute?.title || popupsConstant?.unresolvedDispute?.title,
      description:
        popupData?.unresolvedDispute?.description || popupsConstant?.unresolvedDispute?.description,
      primaryCta:
        popupData?.unresolvedDispute?.primaryCta || popupsConstant?.unresolvedDispute?.primaryCta
    },
    walletLessThanOne: {
      title: popupData?.walletLessThanOne?.title || popupsConstant?.walletLessThanOne?.title,
      description:
        popupData?.walletLessThanOne?.description || popupsConstant?.walletLessThanOne?.description,
      primaryCta:
        popupData?.walletLessThanOne?.primaryCta || popupsConstant?.walletLessThanOne?.primaryCta
    },
    walletWithdraw: {
      title: popupData?.walletWithdraw?.title || popupsConstant?.walletWithdraw?.title,
      description:
        popupData?.walletWithdraw?.description || popupsConstant?.walletWithdraw?.description,
      primaryCta:
        popupData?.walletWithdraw?.primaryCta || popupsConstant?.walletWithdraw?.primaryCta
    }
  };

  return {
    introScreenData: screen1,
    reasonScreenData: screen2,
    closedScreenData: screen3,
    popupsData: popups
  };
};

export const getAccountClosurePopupsCmsData = (
  popupsData: PopupsData,
  { minAmount, currency }: { minAmount: string; currency: string }
): PopupsData => {
  const interpolate = (description: string) =>
    description.replace('{{minAmount}}', minAmount).replace('{{currency}}', currency);

  return {
    amlHit: {
      ...popupsData.amlHit,
      description: interpolate(popupsData.amlHit.description),
      navigationUrl: HELP_CENTER
    },
    negativeBalance: {
      ...popupsData.negativeBalance,
      description: interpolate(popupsData.negativeBalance.description),
      navigationUrl: HELP_CENTER
    },
    transferInProgress: {
      ...popupsData.transferInProgress,
      description: interpolate(popupsData.transferInProgress.description),
      navigationUrl: HISTORY_URL
    },
    unresolvedDispute: {
      ...popupsData.unresolvedDispute,
      description: interpolate(popupsData.unresolvedDispute.description),
      navigationUrl: APP_URL_ONELINK
    },
    walletLessThanOne: {
      ...popupsData.walletLessThanOne,
      description: interpolate(popupsData.walletLessThanOne.description),
      navigationUrl: APP_URL_ONELINK
    },
    walletWithdraw: {
      ...popupsData.walletWithdraw,
      description: interpolate(popupsData.walletWithdraw.description),
      navigationUrl: APP_URL_ONELINK
    }
  };
};

export const getPopupConfig = (
  errorCode: string | undefined,
  popupCmsData: PopupsData
): CloseAccountPopup | null => {
  if (!errorCode) return null;

  switch (errorCode) {
    case CLOSURE_ERROR_CODES.AML_HIT:
      return popupCmsData.amlHit;
    case CLOSURE_ERROR_CODES.NEGATIVE_WALLET_BALANCE:
      return popupCmsData.negativeBalance;
    case CLOSURE_ERROR_CODES.TRANSFER_IN_PROGRESS:
      return popupCmsData.transferInProgress;
    case CLOSURE_ERROR_CODES.WITHDRAW_BALANCE:
      return popupCmsData.walletWithdraw;
    case CLOSURE_ERROR_CODES.ADD_FUNDS_TO_WITHDRAW:
      return popupCmsData.walletLessThanOne;
    case CLOSURE_ERROR_CODES.UNSETTLED_AMAZE_TXN:
      return popupCmsData.unresolvedDispute;
    default:
      return null;
  }
};

export const getRadioOptions = (options: ClosureReasonOption[]): { value: string; label: string }[] =>
  options.map(({ key, text }) => ({ value: key.toString(), label: text }));

export const getCloseAccountPayload = (
  reasonKey: number,
  isConfirmed: boolean,
  isSGAmazeUser: boolean,
  reasonOfClosureMeta: string,
  isOther: boolean
): CloseAccountPayload => ({
  reason_of_closure: reasonKey,
  is_consent_given: isConfirmed,
  ...(isSGAmazeUser && { no_amaze_dispute_confirmed: isConfirmed }),
  ...(isOther && { reason_of_closure_meta: reasonOfClosureMeta || 'N/A' })
});

export const isCloseYourAccountEnabled = () => {
  if (isFlagEnabled(CLOSE_ACCOUNT_FLAG)) {
    try {
      const data = getFlagValue(CLOSE_ACCOUNT_FLAG);
      const whiteListedUsers = data?.whiteListedUsers;
      if (whiteListedUsers?.length > 0 && !whiteListedUsers?.includes(userEmail().toLowerCase())) {
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  } else {
    return false;
  }
};
